import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerSchema } from '../../utils/validation';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Button } from '../../components/common/Button';
import { COUNTRIES } from '../../utils/constants';
import { AlertCircle, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

type RegisterFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  countryCode: string;
  phone?: string;
  affiliateCode?: string;
};

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  const { register, handleSubmit, formState: { errors } } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { affiliateCode: searchParams.get('ref') || '' },
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError('');
    try {
      await authService.register(data);
      toast.success('Registration successful! Please check your email.');
      navigate('/verify-email', { state: { email: data.email } });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-950 via-indigo-800 to-purple-900 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse [animation-delay:1s]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="relative z-10 text-white max-w-md">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 mb-8 text-sm">
            <Sparkles className="h-4 w-4 text-yellow-400" />
            <span className="text-indigo-200">Join the community</span>
          </div>
          <h1 className="text-4xl font-extrabold mb-4 leading-tight">Start winning with RaffleBowl today.</h1>
          <p className="text-indigo-200 text-lg leading-relaxed">Create your free account, pick a raffle, and you could be our next winner.</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-extrabold text-indigo-600">RaffleBowl</h1>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Create your account</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Fill in your details to get started</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mb-6">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Input label="First Name" {...register('firstName')} error={errors.firstName?.message} placeholder="John" />
              <Input label="Last Name" {...register('lastName')} error={errors.lastName?.message} placeholder="Doe" />
            </div>
            <Input label="Email" type="email" {...register('email')} error={errors.email?.message} placeholder="your@email.com" />
            <Input label="Password" type="password" {...register('password')} error={errors.password?.message} placeholder="••••••••" helperText="Minimum 8 characters" />
            <Select label="Country" {...register('countryCode')} error={errors.countryCode?.message}
              options={[{ value: '', label: 'Select a country' }, ...COUNTRIES.map((c) => ({ value: c.code, label: c.name }))]} />
            <Input label="Phone Number (Optional)" {...register('phone')} error={errors.phone?.message} placeholder="+1234567890" helperText="Include country code (e.g., +1 for US)" />
            <Input label="Referral Code (Optional)" {...register('affiliateCode')} error={errors.affiliateCode?.message} placeholder="Enter referral code" />
            <Button type="submit" fullWidth isLoading={isLoading} className="py-3 text-base">Create Account</Button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
